import { jwtDecode } from 'jwt-decode';
import type { FeatureAccess } from '@/lib/sdk/accounts/types';

export interface AuthTokens {
  token: string;
  refreshToken: string;
  org_id?: string;
  user_id?: string;
  role?: string;
  features?: FeatureAccess;
  identity?: string;
}

interface DecodedToken {
  sub: string;
  role: string;
  exp: number;
  businesses?: string[];
}

const cookiePrefix = import.meta.env.VITE_APP_COOKIE_PREFIX


export const getStoredAuth = (): AuthTokens | null => {
  const auth = localStorage.getItem(`${cookiePrefix}_token`);
  return auth ? JSON.parse(auth) : null;
};

export const getTempAuth = (): AuthTokens | null => {
  const auth = localStorage.getItem(`${cookiePrefix}_temp_token`);
  return auth ? JSON.parse(auth) : null;
};


export const setStoredAuth = (tokens: AuthTokens) => {
  localStorage.setItem(`${cookiePrefix}_token`, JSON.stringify(tokens));
};

export const setTempAuth = (tokens: AuthTokens) => {
  localStorage.setItem(`${cookiePrefix}_temp_token`, JSON.stringify(tokens));
};


export const clearStoredAuth = () => {
  localStorage.removeItem(`${cookiePrefix}_token`);
  localStorage.removeItem(`${cookiePrefix}_refresh_token`);
  localStorage.removeItem(`${cookiePrefix}_linked_accounts`);
};

export interface LinkedAccount {
  org_name: string;
  city: string;
  state: string;
  org_address: string;
  user_name: string;
  image_url: string;
  country: string;
  role: string;
  account_id: string;
  features?: FeatureAccess;
}

export const getStoredFeatures = (): FeatureAccess | undefined => {
  const auth = getStoredAuth();
  return auth?.features;
};

export const setLinkedAccounts = (accounts: LinkedAccount[]) => {
  localStorage.setItem(`${cookiePrefix}_linked_accounts`, JSON.stringify(accounts));
};

export const getLinkedAccounts = (): LinkedAccount[] => {
  const data = localStorage.getItem(`${cookiePrefix}_linked_accounts`);
  return data ? JSON.parse(data) : [];
};

export const isTokenValid = (token: string): boolean => {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export const hasRole = (requiredRole: string): boolean => {
  const auth = getStoredAuth();
  if (!auth?.token) return false;

  try {
    const decoded = jwtDecode<DecodedToken>(auth.token);
    return decoded.role === requiredRole;
  } catch {
    return false;
  }
};

export const getBusinesses = (): string[] => {
  const auth = getStoredAuth();
  if (!auth?.token) return [];

  try {
    const decoded = jwtDecode<DecodedToken>(auth.token);
    return decoded.businesses || [];
  } catch {
    return [];
  }
};